// src/pages/AdminDashboard.jsx
import React, { useEffect, useState, useCallback } from "react";
import {
  Table, Button, Modal, Form, Input, Space, Typography,
  Tag, Card, Statistic, Row, Col, Popconfirm, message, Avatar,
  Tooltip, Empty, Spin, Alert,
} from "antd";
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined,
  UserOutlined, MailOutlined, MessageOutlined,
  TeamOutlined, SearchOutlined, EyeOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { ref, push, onValue, update, remove, off } from "firebase/database";
import { db } from "../firebase/firebase";

const { Title, Text } = Typography;
const { TextArea } = Input;

function formatTimestamp(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return ts; }
}

export default function AdminDashboard() {
  const [submissions,   setSubmissions]   = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [dbError,       setDbError]       = useState(null);
  const [modalOpen,     setModalOpen]     = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editRecord,    setEditRecord]    = useState(null);
  const [viewRecord,    setViewRecord]    = useState(null);
  const [searchText,    setSearchText]    = useState("");
  const [form] = Form.useForm();

  // ── Realtime Database listener ────────────────────────────────────────────
  const attachListener = useCallback(() => {
    setLoading(true);
    setDbError(null);
    const submissionsRef = ref(db, "formSubmissions");

    onValue(
      submissionsRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data && typeof data === "object") {
          const list = Object.entries(data).map(([id, val]) => ({
            key: id,
            id,
            name:      val.name      || "",
            email:     val.email     || "",
            message:   val.message   || "",
            timestamp: val.timestamp || null,
            status:    val.status    || "new",
          }));
          list.sort((a, b) => {
            const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
            const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
            return tb - ta;
          });
          setSubmissions(list);
        } else {
          setSubmissions([]);
        }
        setLoading(false);
      },
      (error) => {
        setDbError(error.code || error.message);
        setLoading(false);
      }
    );

    return submissionsRef;
  }, []);

  useEffect(() => {
    const submissionsRef = attachListener();
    return () => off(submissionsRef);
  }, [attachListener]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleRefresh = () => {
    off(ref(db, "formSubmissions"));
    attachListener();
  };

  const openAddModal = () => {
    setEditRecord(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEditModal = (record) => {
    setEditRecord(record);
    form.setFieldsValue({ name: record.name, email: record.email, message: record.message });
    setModalOpen(true);
  };

  const handleSubmit = async (values) => {
    try {
      if (editRecord) {
        await update(ref(db, `formSubmissions/${editRecord.id}`), {
          ...values,
          timestamp: new Date().toISOString(),
        });
        message.success("Updated successfully!");
      } else {
        await push(ref(db, "formSubmissions"), {
          ...values,
          timestamp: new Date().toISOString(),
          status: "new",
        });
        message.success("Entry added!");
      }
      setModalOpen(false);
      form.resetFields();
    } catch (err) {
      message.error("Failed: " + (err.message || err.code));
    }
  };

  const handleDelete = async (id) => {
    try {
      await remove(ref(db, `formSubmissions/${id}`));
      message.success("Deleted!");
    } catch (err) {
      message.error("Delete failed: " + (err.message || err.code));
    }
  };

  const handleStatusToggle = async (record) => {
    const newStatus = record.status === "read" ? "new" : "read";
    try {
      await update(ref(db, `formSubmissions/${record.id}`), { status: newStatus });
    } catch {
      message.error("Update failed");
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const filtered = submissions.filter(
    (s) =>
      s.name.toLowerCase().includes(searchText.toLowerCase()) ||
      s.email.toLowerCase().includes(searchText.toLowerCase()) ||
      s.message.toLowerCase().includes(searchText.toLowerCase())
  );
  const unreadCount = submissions.filter((s) => s.status !== "read").length;

  // ── Table columns ─────────────────────────────────────────────────────────
  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 150,
      render: (text) => (
        <Space size="small">
          <Avatar size={28} icon={<UserOutlined />} />
          <Text strong style={{ fontSize: 13 }}>{text || "—"}</Text>
        </Space>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 210,
      responsive: ["sm"],
      render: (text) => <Text style={{ fontSize: 13 }}>{text || "—"}</Text>,
    },
    {
      title: "Message",
      dataIndex: "message",
      key: "message",
      responsive: ["md"],
      ellipsis: true,
      render: (text) => (
        <Text type="secondary" style={{ fontSize: 13 }} ellipsis={{ tooltip: text }}>
          {text || "—"}
        </Text>
      ),
    },
    {
      title: "Timestamp",
      dataIndex: "timestamp",
      key: "timestamp",
      width: 175,
      responsive: ["lg"],
      render: (ts) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          <ClockCircleOutlined style={{ marginRight: 4 }} />
          {formatTimestamp(ts)}
        </Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 95,
      render: (status, record) => (
        <Tag
          color={status === "read" ? "success" : "processing"}
          style={{ cursor: "pointer" }}
          onClick={() => handleStatusToggle(record)}
        >
          {status === "read" ? "Read" : "New"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 115,
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="View">
            <Button
              type="text" size="small"
              icon={<EyeOutlined />}
              onClick={() => { setViewRecord(record); setViewModalOpen(true); }}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="text" size="small"
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this entry?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes" cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button type="text" size="small" icon={<DeleteOutlined />} danger />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      <Title level={4} style={{ marginBottom: 16 }}>Form Submissions</Title>

      {/* Firebase permission error */}
      {dbError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Firebase Permission Denied"
          description={
            <span>
              Error: <code>{dbError}</code>. Go to{" "}
              <strong>Firebase Console → Realtime Database → Rules</strong> and set{" "}
              <code>{`".read": true, ".write": true`}</code>, then publish.
            </span>
          }
        />
      )}

      {/* Stats row */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {[
          { title: "Total",  value: submissions.length,                                    icon: <TeamOutlined />,    },
          { title: "Unread", value: unreadCount,                                           icon: <MailOutlined />,    },
          { title: "Read",   value: submissions.filter((s) => s.status === "read").length, icon: <MessageOutlined />, },
        ].map((stat) => (
          <Col xs={8} key={stat.title}>
            <Card size="small" style={{ textAlign: "center" }}>
              <Statistic
                title={stat.title}
                value={stat.value}
                prefix={stat.icon}
                valueStyle={{ fontSize: 24 }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Table card */}
      <Card
        size="small"
        extra={
          <Space wrap>
            <Input
              placeholder="Search…"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              style={{ width: 220 }}
              size="small"
            />
            <Button size="small" icon={<ReloadOutlined />} onClick={handleRefresh} />
            <Button size="small" type="primary" icon={<PlusOutlined />} onClick={openAddModal}>
              Add
            </Button>
          </Space>
        }
      >
        <Spin spinning={loading} tip="Loading…">
          <Table
            columns={columns}
            dataSource={filtered}
            size="small"
            scroll={{ x: true }}
            pagination={{
              pageSize: 8,
              showSizeChanger: false,
              showTotal: (t) => `${t} entries`,
              size: "small",
            }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No submissions yet"
                />
              ),
            }}
          />
        </Spin>
      </Card>

      {/* ── Add / Edit Modal ──────────────────────────────────────── */}
      <Modal
        title={editRecord ? "Edit Submission" : "Add Submission"}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        footer={null}
        width={480}
        centered
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ marginTop: 16 }}>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: "Required" }]}>
            <Input prefix={<UserOutlined />} placeholder="Full name" />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Required" },
              { type: "email", message: "Invalid email" },
            ]}
          >
            <Input prefix={<MailOutlined />} placeholder="email@example.com" />
          </Form.Item>
          <Form.Item name="message" label="Message" rules={[{ required: true, message: "Required" }]}>
            <TextArea rows={4} placeholder="Message content…" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={() => { setModalOpen(false); form.resetFields(); }}>Cancel</Button>
              <Button type="primary" htmlType="submit">
                {editRecord ? "Update" : "Add"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* ── View Modal ────────────────────────────────────────────── */}
      <Modal
        title="Submission Details"
        open={viewModalOpen}
        onCancel={() => setViewModalOpen(false)}
        footer={<Button onClick={() => setViewModalOpen(false)}>Close</Button>}
        width={460}
        centered
      >
        {viewRecord && (
          <div style={{ marginTop: 8 }}>
            {[
              { label: "Name",      value: viewRecord.name },
              { label: "Email",     value: viewRecord.email },
              { label: "Timestamp", value: formatTimestamp(viewRecord.timestamp) },
              {
                label: "Status",
                value: (
                  <Tag color={viewRecord.status === "read" ? "success" : "processing"}>
                    {viewRecord.status || "new"}
                  </Tag>
                ),
              },
            ].map((item) => (
              <Row key={item.label} style={{ padding: "8px 0", borderBottom: "1px solid #f0f0f0" }}>
                <Col span={8}><Text type="secondary">{item.label}</Text></Col>
                <Col span={16}><Text strong>{item.value}</Text></Col>
              </Row>
            ))}
            <div style={{ marginTop: 12 }}>
              <Text type="secondary">Message</Text>
              <Card size="small" style={{ marginTop: 6, background: "#fafafa" }}>
                <Text>{viewRecord.message || "—"}</Text>
              </Card>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}